import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiArrowLeft, HiPlus, HiX } from 'react-icons/hi';
import { useAuthHeaders } from '../../hooks/useAuthHeaders';
import './NCAdmin.css';

const ROLES = ['streamer', 'admin'];
const ROLE_LABELS = { admin: 'Admin', streamer: 'Streamer' };

const EMPTY_FORM = { email: '', givenName: '', familyName: '', role: 'streamer', phone: '' };

export default function AdminUsers({ config }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { getAuthHeaders } = useAuthHeaders();

  useEffect(() => {
    getAuthHeaders().then(headers =>
      fetch(`${config.apiUrl}/users`, { headers })
        .then(r => r.json())
        .then(d => setUsers(d.data || []))
        .catch(() => setError('Failed to load users'))
        .finally(() => setLoading(false))
    );
  }, [config.apiUrl]);

  const validate = () => {
    const errs = {};
    if (!form.email) errs.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.givenName) errs.givenName = 'Required';
    if (!form.familyName) errs.familyName = 'Required';
    if (!form.role) errs.role = 'Required';
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setSubmitting(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      // Auto-prefix +61 if Australian number without country code
      let phone = form.phone || undefined;
      if (phone) {
        phone = phone.replace(/\s/g, '');
        if (phone.startsWith('0')) phone = '+61' + phone.slice(1);
        else if (!phone.startsWith('+')) phone = '+61' + phone;
      }
      const res = await fetch(`${config.apiUrl}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ ...form, phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create user');
      setUsers(prev => [...prev, data.data].sort((a, b) =>
        `${a.familyName}${a.givenName}`.localeCompare(`${b.familyName}${b.givenName}`)
      ));
      setSuccess(`${form.givenName} ${form.familyName} created — invite email sent.`);
      setForm(EMPTY_FORM);
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key, label, type = 'text', required = true) => (
    <div className="nc-admin-field">
      <label className="nc-admin-label">{label}{required && ' *'}</label>
      <input
        className={`nc-admin-input${fieldErrors[key] ? ' nc-admin-input--error' : ''}`}
        type={type}
        value={form[key]}
        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setFieldErrors(f => ({ ...f, [key]: '' })); }}
      />
      {fieldErrors[key] && <span className="nc-admin-field-error">{fieldErrors[key]}</span>}
    </div>
  );

  return (
    <div className="nc-admin">
      <Link to="/admin" className="nc-admin-back"><HiArrowLeft /> Admin</Link>
      <div className="nc-admin-eyebrow">NC LIVE</div>
      <div className="nc-admin-title-row">
        <h1 className="nc-admin-title">Users</h1>
        <button className="nc-admin-btn" onClick={() => { setShowForm(s => !s); setError(''); setSuccess(''); }}>
          {showForm ? <><HiX /> Cancel</> : <><HiPlus /> New User</>}
        </button>
      </div>

      {success && <div className="nc-admin-success">{success}</div>}
      {error && <div className="nc-admin-error">{error}</div>}

      {showForm && (
        <form className="nc-admin-form" onSubmit={handleSubmit} noValidate>
          <div className="nc-admin-form-grid">
            {field('givenName', 'First Name')}
            {field('familyName', 'Last Name')}
            {field('email', 'Email', 'email')}
            {field('phone', 'Phone (optional)', 'tel', false)}
            <div className="nc-admin-field">
              <label className="nc-admin-label">Role *</label>
              <select
                className={`nc-admin-select${fieldErrors.role ? ' nc-admin-input--error' : ''}`}
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              >
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
          </div>
          <button className="nc-admin-btn nc-admin-btn--primary" type="submit" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create User'}
          </button>
        </form>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
          <div className="loading-spinner" />
        </div>
      ) : users.length === 0 ? (
        <div className="nc-admin-empty">No users yet. Create the first one above.</div>
      ) : (
        <div className="nc-admin-list">
          {users.map(u => (
            <div key={u.userId} className="nc-admin-row">
              <div className="nc-admin-row-main">
                <span className="nc-admin-row-title">{u.givenName} {u.familyName}</span>
                <span className="nc-admin-row-meta">{u.email}{u.phone ? ` · ${u.phone}` : ''}</span>
              </div>
              <span className={`nc-admin-badge nc-admin-badge--${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
